import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, Groupper, Header, Message, Table, Icon, Modal, useFrontier, Toolbar } from "react-frontier2";
import { CatalogEvent, SetLoading } from '@arema/components/Classes';
import { useParams } from "react-router-dom";
import { bindClick } from '@arema/components/Util';
import { ErrorHeader, SearchEventModal, SearchPromoterModal, UserReportsAccessModal } from '../../components';
import { useUser } from '../../AdminHooks';
import { Promoter, UserReportsEvent, UserReportsPromoter } from '../../AdminClasses';
import { useTranslation } from 'react-i18next';
import API from "./../../API";
import UserAccess from '../../UserAccess';
import moment from 'moment';
import ReportsAccess from '../../ReportsAccess';

enum Modals {
	SEARCH_EVENT = 1,
	SEARCH_PROMOTER = 2,
	ACCESS = 20,
	DELETE_ACCESS = 21
}

var UserEvents = ()=>{
	var params = useParams();
	var { t } = useTranslation();
	var { user, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [userEvents, setUserEvents] = useState<UserReportsEvent[]>(null);
	var [userPromoters, setUserPromoters] = useState<UserReportsPromoter[]>(null);
	var [selectedEvent, setSelectedEvent] = useState<UserReportsEvent>(null);
	var [selectedPromoter, setSelectedPromoter] = useState<UserReportsPromoter>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [refreshing, setRefreshing] = useState<boolean>(false);
	var user_id = parseInt(params.id);
	var userEventsRef = useRef<typeof userEvents>(null);
	var userPromotersRef = useRef<typeof userPromoters>(null);
	userEventsRef.current = userEvents;
	userPromotersRef.current = userPromoters;

	useEffect(()=>{
		if(!user_id || Number.isNaN(user_id)) return;
		API.getUserReportsAccess(user_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUserEvents(res.data.events);
			setUserPromoters(res.data.promoters);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'USREV-LOD-1' }))
		});
	}, []);

	useEffect(()=>{
		setModalPrompts(null);
	}, [shownModal]);

	var onEventSelected = (event: CatalogEvent): boolean=>{
		var new_event : UserReportsEvent = {
			event_id: event.event_id,
			event_name: event.event_name,
			// user_added: user.user_id,
			date_added: moment().unix(),
			access: [
				ReportsAccess.VIEW_STATS,
				ReportsAccess.VIEW_INCOME,
				ReportsAccess.VIEW_TICKET_USES,
				ReportsAccess.VIEW_SEATMAP,
				ReportsAccess.DOWNLOAD_REPORTS,
			]
		}
		setShownModal(Modals.ACCESS);
		setSelectedEvent(new_event);
		return true;
	}

	var onPromoterSelected = (promoter: Promoter): boolean=>{
		var new_promoter : UserReportsPromoter = {
			promoter_id: promoter.promoter_id,
			promoter_name: promoter.promoter_name,
			// user_added: user.user_id,
			date_added: moment().unix(),
			access: [
				ReportsAccess.VIEW_STATS,
				ReportsAccess.VIEW_INCOME,
				ReportsAccess.VIEW_TICKET_USES,
				ReportsAccess.VIEW_SEATMAP,
				ReportsAccess.DOWNLOAD_REPORTS,
			]
		}
		setShownModal(Modals.ACCESS);
		setSelectedPromoter(new_promoter);
		return true;
	}

	var showDeleteEvent = (ev: UserReportsEvent)=>{
		return ()=>{
			if(!ev) return;
			setShownModal(Modals.DELETE_ACCESS);
			setSelectedEvent(ev);
		}
	}

	var showRemovePromoter = (pr: UserReportsPromoter)=>{
		return ()=>{
			if(!pr) return;
			setShownModal(Modals.DELETE_ACCESS);
			setSelectedPromoter(pr);
		}
	}

	var removeEvent = (setLoading: SetLoading)=>{
		if(!selectedEvent) return;
		setLoading(true);
		API.removeUserReportsEvent(user_id, selectedEvent.event_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(userPromoters){
				setUserEvents(userEvents.filter(a=>a.event_id!==selectedEvent.event_id));
			}
			Toast.success(`Se ha eliminado el evento "${selectedEvent.event_name}" del usuario`);
			setSelectedEvent(null);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_code', { code: 'USREV-RMEVT-1' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var removePromoter = (setLoading: SetLoading)=>{
		if(!selectedPromoter) return;
		setLoading(true);
		API.removeUserReportsPromoter(user_id, selectedPromoter.promoter_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(userPromoters){
				setUserPromoters(userPromoters.filter(a=>a.promoter_id!==selectedPromoter.promoter_id));
			}
			Toast.success(`Se ha eliminado el promotor "${selectedPromoter.promoter_name}" del usuario`);
			setSelectedPromoter(null);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_code', { code: 'USREV-RMPRM-1' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var closeDelete = ()=>{
		setSelectedEvent(null);
		setSelectedPromoter(null);
		setShownModal(null);
	}

	var showEventAccess = (event: UserReportsEvent)=>{
		return ()=>{
			setSelectedEvent(event);
			setShownModal(Modals.ACCESS);
		}
	}

	var showPromoterAccess = (promoter: UserReportsPromoter)=>{
		return ()=>{
			setSelectedPromoter(promoter)
			setShownModal(Modals.ACCESS);
		}
	}

	var onEventAccessChange = (event: UserReportsEvent)=>{
		setSelectedEvent(null);
		setSelectedPromoter(null);
		setShownModal(null);

		var tl = Toast.show(`Guardando permisos de evento "${event.event_name}"`, { loading: true });
		API.addUserReportsEvent(user_id, event.event_id, event.access).then(res=>{
			if(res.error) return Toast.error(res.message);

			var ue = [...userEventsRef.current];
			var ix = ue.findIndex(a=>a.event_id===event.event_id);
			if(ix==-1){
				ue.push(event);
			}else{
				ue[ix].access = event.access;
			}
			setUserEvents(ue);
			Toast.success(`Se ha agregado permisos del evento "${event.event_name}"`)
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'USREV-OEAC-2' }));
		}).finally(()=>{
			Toast.dismiss(tl);
		})
	}

	var onPromoterAccessChange = (promoter: UserReportsPromoter)=>{
		setSelectedEvent(null);
		setSelectedPromoter(null);
		setShownModal(null);

		var tl = Toast.show(`Guardando permisos de promotor "${promoter.promoter_name}"`, { loading: true });
		API.addUserReportsPromoter(user_id, promoter.promoter_id, promoter.access).then(res=>{
			if(res.error) return Toast.error(res.message);

			var up = [...userPromotersRef.current];
			var ix = up.findIndex(a=>a.promoter_id===promoter.promoter_id);
			if(ix==-1){
				up.push(promoter);
			}else{
				up[ix].access = promoter.access;
			}
			setUserPromoters(up);
			Toast.success(`Se ha agregado permisos del promotor "${promoter.promoter_name}"`)
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'USREV-OPEA-2' }));
		}).finally(()=>{
			Toast.dismiss(tl);
		})
	}

	var sendEventRefresh = ()=>{
		if(refreshing) return;
		setRefreshing(true);
		var tx = Toast.show('Refrescando eventos...', {
			loading: true,
			dismissable: false
		});
		API.refreshUserEvents(user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se han refrescado los eventos del usuario');
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'USREV-RFEV-1' }));
		}).finally(()=>{
			setRefreshing(false);
			Toast.dismiss(tx);
		})
	}

	var has_edit_access = useMemo(()=>{
		return hasAccess(UserAccess.USERS.EDIT_REPORTS_ACCESS);
	}, [user])
	
	if(loadError){
		return <ErrorHeader error={loadError} />
	}
	if(!userEvents || !userPromoters){
		return <Header loading text={t('common.loading')} />
	}

	return <>
		<Groupper title={'Empresarios'} style={{ maxWidth: 600, margin: 'auto' }}>
			<Toolbar stretch={false}>
				<Toolbar.Item text='Refrescar eventos' disabled={refreshing} iconName='sync' onClick={sendEventRefresh} />
			</Toolbar>
			<Table style={{ borderTop: 0, borderTopLeftRadius: 8, borderTopRightRadius: 8 }} fitted removeBorder={['bottom']} top>
				<Table.Head 
					title='Eventos' 
					titleRight={has_edit_access && (
						<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={bindClick(setShownModal, Modals.SEARCH_EVENT)} />
					)} 
					collapsingIndexes={[0, 2, 3]}
					centeredIndexes={[3]}
					data={[['ID', 'Nombre', 'Permisos', <Icon name='pen' />]]}
				/>
				<Table.Body>
					{userEvents.length===0 && (
						<Table.Cell row empty colSpan={100}>
							Este usuario no tiene eventos asignados
						</Table.Cell>
					)}
					{userEvents.map(a=>(
						<Table.Row
							collapsingIndexes={[0, 2, 3]}
							centeredIndexes={[3]}
							data={[
								a.event_id,
								a.event_name,
								`${a.access?.length || 0} permiso${a.access?.length===1 ? '' : 's'}`,
								has_edit_access && (
									<Button icon color='black' iconName="pen" size="tiny" menu={[
										{ text: 'Permisos', iconName: 'lock', onClick: showEventAccess(a) },
										{ text: 'Eliminar', iconName: 'remove', onClick: showDeleteEvent(a) },
									]} />
								)
							]}
						/>
					))}
				</Table.Body>
			</Table>
			<Table style={{ marginTop: 15 }} fitted last>
				<Table.Head
					title='Promotores'
					collapsingIndexes={[0, 2, 3]}
					centeredIndexes={[3]}
					data={[['ID', 'Promotor', 'Permisos', <Icon name='pen' />]]}
					titleRight={has_edit_access && (
						<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={bindClick(setShownModal, Modals.SEARCH_PROMOTER)} />
					)}
				/>
				<Table.Body>
					{userPromoters.length===0 && (
						<Table.Cell row empty colSpan={100}>
							Este usuario no tiene promotores asignados
						</Table.Cell>
					)}
					{userPromoters.map(a=>(
						<Table.Row
							collapsingIndexes={[0, 2, 3]}
							centeredIndexes={[3]}
							data={[
								a.promoter_id,
								a.promoter_name,
								`${a.access?.length || 0} permiso${a.access?.length===1 ? '' : 's'}`,
								has_edit_access && (
									<Button icon color='black' iconName="pen" size="tiny" menu={[
										{ text: 'Permisos', iconName: 'lock', onClick: showPromoterAccess(a) },
										{ text: 'Eliminar', iconName: 'remove', onClick: showRemovePromoter(a) },
									]} />
								)
							]} 
						/>
					))}
				</Table.Body>
			</Table>
		</Groupper>

		<SearchEventModal
			open={shownModal === Modals.SEARCH_EVENT}
			onClose={bindClick(setShownModal)}
			title='Buscar Evento'
			onEventSelected={onEventSelected}
		/>

		<SearchPromoterModal
			open={shownModal === Modals.SEARCH_PROMOTER}
			onClose={bindClick(setShownModal)}
			title='Buscar Promotor'
			onSelected={onPromoterSelected}
		/>

		{has_edit_access && (
			<UserReportsAccessModal
				open={(!!selectedEvent || !!selectedPromoter) && shownModal!==Modals.DELETE_ACCESS}
				onClose={()=>{
					setSelectedEvent(null);
					setSelectedPromoter(null);
				}}
				event={selectedEvent}
				promoter={selectedPromoter}
				onEventEdit={onEventAccessChange}
				onPromoterEdit={onPromoterAccessChange}
			/>	
		)}

		<Modal open={shownModal===Modals.DELETE_ACCESS} onClose={closeDelete} size='mini'>
			<Modal.Header>Eliminar acceso {selectedEvent ? 'evento' : 'promotor'}</Modal.Header>
			<Modal.Content>
				<Header text={`¿Eliminar acceso a ${selectedEvent ? 'evento' : 'promotor'}?`} subtext={`Se le quitarán todos los accesos al ${selectedEvent ? 'evento' : 'promotor'} "${selectedEvent?.event_name || selectedPromoter?.promoter_name}"`} />
				<Message type={'error'} list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={closeDelete} />
				<Button text='Eliminar' color={'red'} onClick={selectedEvent ? removeEvent : removePromoter} />
			</Modal.Actions>
		</Modal>
	</>
}

export default UserEvents;
